<template>
  <v-row>
    <v-col>
      <v-row dense>
        <v-col class="text-h5 gray--text">
          Anestesia
        </v-col>
      </v-row>

      <v-row dense>
        <v-col cols="6">
          <v-combobox
            v-model="anesthetist"
            label="Anestesista"
            dense
            hide-details
            outlined
            :items="doctorUsers"
            item-text="employee.name"
            background-color="white"
            :disabled="disabled"
            @change="handleAnesthetistChange"
          />
        </v-col>

        <v-col cols="6">
          <v-combobox
            v-model="assistants"
            label="Auxiliares"
            dense
            hide-details
            outlined
            :items="nurseUsers"
            item-text="employee.name"
            multiple
            chips
            deletable-chips
            small-chips
            background-color="white"
            :disabled="disabled"
            @change="handleAssistantsChange"
          />
        </v-col>
      </v-row>

      <v-row dense>
        <v-col>
          <v-combobox
            v-model="descriptionModel"
            label="Anestesista"
            dense
            hide-details
            outlined
            :items="descriptionModels"
            item-text="name"
            background-color="white"
            :disabled="disabled"
          />
        </v-col>

        <v-col>
          <v-combobox
            v-model="descriptionTemplate"
            label="Modelo de anestesista"
            dense
            hide-details
            outlined
            :items="descriptionModelTemplates"
            item-text="name"
            background-color="white"
            :disabled="disabled"
            @change="choseDescriptionTemplate"
          />
        </v-col>
      </v-row>

      <v-row dense>
        <v-col>
          <RichText
            v-model="description"
            :disabled="disabled"
            @input="handleDescriptionChange"
            @change="handleDescriptionChange"
          />
        </v-col>
      </v-row>

      <v-row dense>
        <v-col class="text-h5 gray--text">
          Anexos Anestesia
        </v-col>
      </v-row>

      <v-row dense>
        <v-col>
          <FileUpload
            ownerType="Surgery::Intraoperative::Anesthesia"
            :ownerId="anesthesia.id"
            :disabled="disabled"
          />
        </v-col>
      </v-row>
    </v-col>
  </v-row>
</template>

<script>
export default {
  name: "SurgeryIntraoperativeAnesthesia",

  props: {
    attendance: Object,
    surgery: Object,
    refreshSurgery: Function,
    doctorUsers: Array,
    nurseUsers: Array,

    disabled: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      /*
       * O componente inicia bloqueando a detecção de alterações.
       */
      initializing: true,

      /*
       * Estado local informado ao componente pai.
       */
      hasUnsavedChanges: false,

      /*
       * Snapshot dos dados persistidos.
       *
       * IMPORTANTE:
       * hydrateUsers() NÃO modifica este snapshot.
       */
      initialSnapshot: null,

      /*
       * Controla apenas a hidratação programática
       * dos comboboxes.
       */
      hydratingUsers: false,

      /*
       * Permitem distinguir:
       *
       * - campo ainda não carregado;
       * - campo que o usuário realmente alterou.
       */
      anesthetistTouched: false,
      assistantsTouched: false,

      anesthetist: null,
      assistants: [],

      description: "",

      descriptionModel: null,
      descriptionTemplate: null,
    };
  },

  computed: {
    anesthesia() {
      return (
        this.surgery?.intraoperative?.anesthesia ||
        {}
      );
    },

    /*
     * Observamos os IDs, e não a referência do array.
     *
     * O pai pode reconstruir doctorUsers/nurseUsers
     * durante renders sem que os dados tenham realmente
     * mudado.
     */
    doctorUsersSignature() {
      return (this.doctorUsers || [])
        .map((user) =>
          String(user?.id || "")
        )
        .filter(Boolean)
        .sort()
        .join("|");
    },

    nurseUsersSignature() {
      return (this.nurseUsers || [])
        .map((user) =>
          String(user?.id || "")
        )
        .filter(Boolean)
        .sort()
        .join("|");
    },

    descriptionModels() {
      return this.lodash.uniqBy(
        this.lodash.map(
          this
            .surgeryIntraoperativeAnesthesiaTypeTemplates ||
            [],
          "anesthesiaType"
        ),
        "id"
      );
    },

    descriptionModelTemplates() {
      if (!this.descriptionModel?.id) {
        return [];
      }

      return this.lodash.filter(
        this
          .surgeryIntraoperativeAnesthesiaTypeTemplates ||
          [],
        (template) =>
          template.anesthesiaType?.id ===
          this.descriptionModel.id
      );
    },
  },

  watch: {
    /*
     * Quando as listas chegarem do pai,
     * preenchemos os comboboxes.
     *
     * Isso NÃO redefine o baseline.
     */
    doctorUsersSignature() {
      this.hydrateUsers();
    },

    nurseUsersSignature() {
      this.hydrateUsers();
    },
  },

  async mounted() {
    this.initializing = true;

    /*
     * Primeiro carregamos o texto persistido.
     */
    this.description =
      this.anesthesia.description || "";

    /*
     * O baseline vem diretamente do estado persistido,
     * não dos componentes de UI.
     *
     * Essa é a proteção principal contra a race condition
     * observada em produção.
     */
    this.capturePersistedSnapshot();

    /*
     * Tenta resolver anestesista e auxiliares com as listas
     * disponíveis neste momento.
     *
     * Caso elas ainda não tenham chegado, os watchers acima
     * executarão hydrateUsers() posteriormente.
     */
    await this.hydrateUsers();

    await this.$nextTick();

    this.hasUnsavedChanges = false;

    /*
     * Garante que o pai comece sincronizado.
     */
    this.$emit("dirty-change", false);

    this.initializing = false;
  },

  apollo: {
    surgeryIntraoperativeAnesthesiaTypeTemplates: {
      query: require(
        "@/graphql/surgery/surgery_intraoperative_anesthesia_type_templates.gql"
      ),
    },
  },

  methods: {
    normalizeId(value) {
      if (
        value === null ||
        value === undefined ||
        value === ""
      ) {
        return null;
      }

      return String(value);
    },

    normalizeIds(values) {
      return (values || [])
        .map((value) =>
          this.normalizeId(value)
        )
        .filter(Boolean)
        .sort();
    },

    normalizeRichText(value) {
      let normalized = String(value || "")
        .replace(/\u00a0/g, " ")
        .replace(/&nbsp;/gi, " ")
        .replace(
          /<(p|div)>\s*(<br\s*\/?>)?\s*<\/\1>/gi,
          ""
        )
        .replace(/>\s+</g, "><")
        .trim();

      /*
       * Evita falso dirty quando o RichText transforma:
       *
       * Texto
       *
       * em:
       *
       * <p>Texto</p>
       */
      if (
        /^<p>[\s\S]*<\/p>$/i.test(
          normalized
        ) &&
        !/<\/p>\s*<p>/i.test(normalized)
      ) {
        normalized = normalized
          .replace(/^<p>/i, "")
          .replace(/<\/p>$/i, "")
          .trim();
      }

      if (
        /^<div>[\s\S]*<\/div>$/i.test(
          normalized
        ) &&
        !/<\/div>\s*<div>/i.test(
          normalized
        )
      ) {
        normalized = normalized
          .replace(/^<div>/i, "")
          .replace(/<\/div>$/i, "")
          .trim();
      }

      return normalized;
    },

    /*
     * Cria o snapshot diretamente dos dados
     * persistidos recebidos pelo backend.
     *
     * Não depende de doctorUsers/nurseUsers.
     */
    createPersistedSnapshot() {
      return {
        anesthetistId: this.normalizeId(
          this.anesthesia.anesthetist?.id
        ),

        assistantIds: this.normalizeIds(
          this.lodash.map(
            this.anesthesia.assistants || [],
            "id"
          )
        ),

        description: this.normalizeRichText(
          this.anesthesia.description
        ),
      };
    },

    capturePersistedSnapshot() {
      this.initialSnapshot =
        this.createPersistedSnapshot();
    },

    /*
     * Retorna o ID atualmente selecionado.
     */
    selectedAnesthetistId() {
      return this.normalizeId(
        this.lodash.get(
          this.anesthetist,
          "id",
          null
        )
      );
    },

    selectedAssistantIds() {
      return this.normalizeIds(
        this.lodash.map(
          this.assistants || [],
          "id"
        )
      );
    },

    /*
     * Cria o snapshot atual.
     *
     * Para campos ainda NÃO tocados pelo usuário,
     * usa o baseline persistido.
     *
     * Isso evita que:
     *
     * anesthetist === null
     *
     * enquanto doctorUsers ainda está carregando
     *
     * seja interpretado como remoção do anestesista.
     */
    createCurrentSnapshot() {
      const baseline =
        this.initialSnapshot || {
          anesthetistId: null,
          assistantIds: [],
          description: "",
        };

      return {
        anesthetistId:
          this.anesthetistTouched
            ? this.selectedAnesthetistId()
            : baseline.anesthetistId,

        assistantIds:
          this.assistantsTouched
            ? this.selectedAssistantIds()
            : [...baseline.assistantIds],

        description: this.normalizeRichText(
          this.description
        ),
      };
    },

    snapshotsAreEqual(left, right) {
      return (
        JSON.stringify(left) ===
        JSON.stringify(right)
      );
    },

    updateDirtyState() {
      if (
        this.initializing ||
        this.initialSnapshot === null
      ) {
        return;
      }

      const currentSnapshot =
        this.createCurrentSnapshot();

      const isDirty =
        !this.snapshotsAreEqual(
          currentSnapshot,
          this.initialSnapshot
        );

      this.hasUnsavedChanges = isDirty;

      /*
       * Este evento é o contrato com intraoperative.vue.
       */
      this.$emit(
        "dirty-change",
        isDirty
      );
    },

    /*
     * RichText:
     *
     * usamos input + change.
     *
     * input captura digitação imediatamente.
     * change permanece como fallback para a implementação
     * atual do editor.
     */
    handleDescriptionChange() {
      if (
        this.initializing ||
        this.disabled
      ) {
        return;
      }

      this.$nextTick(() => {
        if (
          this.initializing ||
          this.disabled
        ) {
          return;
        }

        this.updateDirtyState();
      });
    },

    handleAnesthetistChange() {
      if (
        this.initializing ||
        this.disabled ||
        this.hydratingUsers
      ) {
        return;
      }

      this.$nextTick(() => {
        if (
          this.initializing ||
          this.disabled ||
          this.initialSnapshot === null
        ) {
          return;
        }

        /*
         * Só consideramos "touched" se o valor
         * efetivamente divergir do baseline.
         *
         * Isso também protege contra change programático
         * emitido pelo v-combobox.
         */
        this.anesthetistTouched =
          this.selectedAnesthetistId() !==
          this.initialSnapshot.anesthetistId;

        this.updateDirtyState();
      });
    },

    handleAssistantsChange() {
      if (
        this.initializing ||
        this.disabled ||
        this.hydratingUsers
      ) {
        return;
      }

      this.$nextTick(() => {
        if (
          this.initializing ||
          this.disabled ||
          this.initialSnapshot === null
        ) {
          return;
        }

        const currentIds =
          this.selectedAssistantIds();

        this.assistantsTouched =
          JSON.stringify(currentIds) !==
          JSON.stringify(
            this.initialSnapshot.assistantIds
          );

        this.updateDirtyState();
      });
    },

    /*
     * Apenas transforma IDs persistidos em objetos
     * necessários pelos v-combobox.
     *
     * NÃO:
     *
     * - altera initialSnapshot;
     * - altera description;
     * - limpa dirty;
     * - dispara mutation.
     */
    loadUsers() {
      if (
        !this.anesthetistTouched
      ) {
        const anesthetistId =
          this.initialSnapshot
            ?.anesthetistId;

        if (anesthetistId) {
          this.anesthetist =
            this.lodash.find(
              this.doctorUsers || [],
              (user) =>
                this.normalizeId(user.id) ===
                anesthetistId
            ) || null;
        } else {
          this.anesthetist = null;
        }
      }

      if (
        !this.assistantsTouched
      ) {
        const assistantIds =
          this.initialSnapshot
            ?.assistantIds || [];

        this.assistants =
          this.lodash.filter(
            this.nurseUsers || [],
            (user) =>
              assistantIds.includes(
                this.normalizeId(user.id)
              )
          );
      }
    },

    async hydrateUsers() {
      this.hydratingUsers = true;

      try {
        /*
         * Carregamos somente campos que o operador
         * ainda não modificou.
         */
        this.loadUsers();

        await this.$nextTick();
      } finally {
        this.hydratingUsers = false;
      }

      /*
       * NÃO existe captureSnapshot() aqui.
       *
       * Esse detalhe elimina a race condition:
       *
       * usuário digita
       *       ↓
       * usuários chegam
       *       ↓
       * hydrateUsers()
       *       ↓
       * baseline permanece intacto
       */
    },

    async save() {
      if (
        this.disabled ||
        !this.hasUnsavedChanges
      ) {
        return;
      }

      /*
       * Guarda exatamente o estado que será enviado.
       */
      const snapshotBeingSaved =
        this.createCurrentSnapshot();

      await this.apolloMutate({
        mutation: require(
          "@/graphql/surgery/save_surgery_intraoperative_anesthesia.gql"
        ),

        variables: {
          surgeryId: this.surgery.id,

          /*
           * Usamos o snapshot, não diretamente
           * o objeto do combobox.
           *
           * Se doctorUsers ainda não tiver chegado,
           * preservamos corretamente o ID persistido.
           */
          anesthetistId:
            snapshotBeingSaved
              .anesthetistId || "",

          description:
            this.description,

          assistantIds:
            snapshotBeingSaved
              .assistantIds,
        },
      });

      this.descriptionModel = null;
      this.descriptionTemplate = null;

      /*
       * O estado efetivamente enviado passa a ser
       * o novo baseline.
       */
      this.initialSnapshot =
        snapshotBeingSaved;

      /*
       * Recalculamos touched porque o usuário pode
       * ter alterado campos enquanto a request estava
       * em andamento.
       */
      this.anesthetistTouched =
        this.selectedAnesthetistId() !==
        this.initialSnapshot.anesthetistId;

      const currentAssistantIds =
        this.selectedAssistantIds();

      this.assistantsTouched =
        JSON.stringify(
          currentAssistantIds
        ) !==
        JSON.stringify(
          this.initialSnapshot.assistantIds
        );

      /*
       * Se os objetos ainda não estavam hidratados,
       * não devemos considerar null/[] como edição.
       */
      if (
        !this.anesthetist &&
        this.initialSnapshot
          .anesthetistId &&
        !this.anesthetistTouched
      ) {
        this.anesthetistTouched = false;
      }

      this.updateDirtyState();

      /*
       * Caso tudo enviado continue sendo exatamente
       * o estado atual, limpamos os flags de edição.
       */
      if (!this.hasUnsavedChanges) {
        this.anesthetistTouched = false;
        this.assistantsTouched = false;

        this.$emit(
          "dirty-change",
          false
        );
      }
    },

    choseDescriptionTemplate() {
      if (
        !this.descriptionTemplate
          ?.description
      ) {
        return;
      }

      if (!this.description) {
        this.description =
          this.descriptionTemplate.description;
      } else {
        this.description += "\n";

        this.description +=
          this.descriptionTemplate.description;
      }

      this.handleDescriptionChange();
    },
  },
};
</script>

<style lang="scss" scoped></style>
